// src/riskScore.js
// JBF Risk Score — pure questionnaire data + scoring logic (no React).
// Composite 1–100 = weighted average of (option score × question weight) / Σ weights.
// Sub-scores are the weighted average within each category. Weights sum to 1.00.

export const RISK_CATEGORIES = [
  { key: 'lossTolerance', label: 'Loss Tolerance' },
  { key: 'riskCapacity',  label: 'Capacity to Take Risk' },
  { key: 'rothContext',   label: 'Roth Conversion Context' },
];

export const RISK_QUESTIONS = [
  // ── LOSS TOLERANCE (0.15, 0.15, 0.10) ──────────────────────────────────────
  {
    id: 'q1', category: 'lossTolerance', weight: 0.15,
    prompt: 'If your investment portfolio dropped 20% in a single year, what would you most likely do?',
    options: [
      { label: 'Sell immediately — protecting what I have is more important than potential recovery', score: 10 },
      { label: 'Move a portion to safer investments and wait to see what happens', score: 30 },
      { label: "Hold steady — I'd be uncomfortable, but I'd stay the course", score: 60 },
      { label: 'Buy more — a drop like that is an opportunity', score: 90 },
    ],
  },
  {
    id: 'q2', category: 'lossTolerance', weight: 0.15,
    prompt: 'Which best describes how you think about investment risk?',
    options: [
      { label: 'I prioritize preserving what I have, even if that limits growth', score: 10 },
      { label: "I'm willing to accept modest swings for moderate growth", score: 35 },
      { label: 'I expect significant short-term volatility in exchange for strong long-term returns', score: 70 },
      { label: "I'm focused entirely on maximizing long-term growth — short-term drops don't concern me", score: 95 },
    ],
  },
  {
    id: 'q3', category: 'lossTolerance', weight: 0.10,
    prompt: 'Two portfolios, same 10-year window. Portfolio A grows steadily at 5% per year. Portfolio B averages 9% but swings between -25% and +35% in any given year. Which do you choose?',
    options: [
      { label: "Portfolio A — I'd rather have the predictable outcome", score: 15 },
      { label: "Closer to A, but I'd accept a little more volatility for a bit more return", score: 40 },
      { label: 'Portfolio B — the higher average return is worth the ride', score: 80 },
      { label: "Definitely Portfolio B — I won't even look at the annual statements", score: 95 },
    ],
  },
  // ── CAPACITY TO TAKE RISK (0.10, 0.15, 0.05) ───────────────────────────────
  // Q5 scores the horizon of the CONVERTED money (the Roth being built — typically
  // last-money-out / heir money), NOT retirement proximity. Reworded Aug 4 2026:
  // the old "years until you draw from this retirement account" gave nearly every
  // 59+ prospect the worst score and flattened the tier distribution toward T1.
  {
    id: 'q4', category: 'riskCapacity', weight: 0.10,
    prompt: 'How would you describe your retirement income situation?',
    options: [
      { label: 'Very stable — all the income I need in retirement is guaranteed (pension, Social Security, annuity)', score: 85 },
      { label: 'Mostly stable — I have a small income gap that will come from my retirement savings each month', score: 60 },
      { label: 'Somewhat stable — I have a sizable income gap that my savings will need to fill', score: 35 },
      { label: "I have Social Security (and possibly a pension), but it covers less than half of what I need — I'm relying heavily on savings to fill the gap", score: 10 },
    ],
  },
  {
    id: 'q5', category: 'riskCapacity', weight: 0.15,
    prompt: 'The account this strategy builds is your tax-free Roth. Realistically, when do you expect to actually spend those dollars?',
    options: [
      { label: 'Within the next 5 years', score: 10 },
      { label: 'In 5–10 years', score: 40 },
      { label: 'In 10–20 years', score: 70 },
      { label: "Mostly never — it's for my spouse and heirs", score: 90 },
    ],
  },
  {
    id: 'q6', category: 'riskCapacity', weight: 0.05,
    prompt: 'What percentage of your total net worth does this retirement account represent?',
    options: [
      { label: "More than 80% — it's essentially all I have", score: 10 },
      { label: "50–80% — it's the majority", score: 35 },
      { label: '25–50% — meaningful, but I have other assets', score: 65 },
      { label: 'Less than 25% — I have substantial assets outside this account', score: 85 },
    ],
  },
  // ── ROTH CONVERSION CONTEXT (0.15, 0.08, 0.07) ─────────────────────────────
  {
    id: 'q7', category: 'rothContext', weight: 0.15,
    prompt: 'How concerned are you about the possibility that income tax rates will be higher in retirement than they are today?',
    options: [
      { label: 'Not concerned — I expect my tax rate to drop significantly in retirement', score: 10 },
      { label: 'Mildly concerned — I think rates may be similar', score: 35 },
      { label: 'Moderately concerned — I think tax rates will likely rise', score: 65 },
      { label: 'Very concerned — I consider future tax increases a near-certainty', score: 90 },
    ],
  },
  {
    id: 'q8', category: 'rothContext', weight: 0.08,
    prompt: "Imagine a strategy that generates monthly income to pay your Roth conversion taxes — but the underlying fund value fluctuates. If the income payments arrived as expected but the fund's value was down 15% in a given month, how would you feel?",
    options: [
      { label: 'Not concerned — the income is what matters, not the daily value', score: 90 },
      { label: "I'd want a brief explanation of why it's down", score: 65 },
      { label: "I'd be concerned and would want to reassess the plan", score: 30 },
      { label: 'That level of volatility would not work for me', score: 5 },
    ],
  },
  {
    id: 'q9', category: 'rothContext', weight: 0.07,
    prompt: 'Outside of your IRA or 401(k), how would you describe your liquid savings?',
    options: [
      { label: 'Limited — most of my wealth is inside retirement accounts', score: 20 },
      { label: 'Modest — enough for emergencies but not much beyond that', score: 45 },
      { label: 'Comfortable — I have meaningful cash or taxable investments', score: 75 },
      { label: 'Substantial — I have significant assets outside retirement accounts', score: 90 },
    ],
  },
];

// Band for a 1–100 score — THREE bands aligned 1:1 with the CE portfolio tiers
// (cutoffs locked Aug 4 2026; mixes retuned Aug 6 2026): ≤40 Conservative → T1
// Balanced Income (9%), 41–70 Moderate → T2 Income Blend (10%), 71–100 Growth →
// T3 Enhanced Income (11%). Same cutoffs as
// tierForRiskScore in calcEngine.js so score → band → portfolio always agree.
export function bandFor(score) {
  if (score <= 40) return 'Conservative';
  if (score <= 70) return 'Moderate';
  return 'Growth';
}

// CE fit signal — driven by Q8 (volatility-of-conversion-funding) score only.
export function ceSignalFor(q8Score) {
  if (q8Score >= 65) return { level: 'green',  label: 'Likely fit',      text: 'Convert Engine: likely fit' };
  if (q8Score >= 30) return { level: 'yellow', label: 'Discuss further', text: 'Convert Engine: discuss further' };
  return { level: 'red', label: 'Not a fit', text: 'Convert Engine: not a fit' };
}

// answers: { q1: optionIndex, … } — every question must be answered.
export function isComplete(answers) {
  return RISK_QUESTIONS.every(q => Number.isInteger(answers?.[q.id]));
}

export function computeRiskScore(answers) {
  const byCat = { lossTolerance: [], riskCapacity: [], rothContext: [] };
  let weightedSum = 0, totalWeight = 0;

  RISK_QUESTIONS.forEach(q => {
    const idx = answers[q.id];
    const s = q.options[idx].score;
    weightedSum += s * q.weight;
    totalWeight += q.weight;
    byCat[q.category].push({ score: s, weight: q.weight });
  });

  const subScore = arr => {
    const ws = arr.reduce((a, x) => a + x.score * x.weight, 0);
    const tw = arr.reduce((a, x) => a + x.weight, 0);
    return Math.round(ws / tw);
  };

  const composite = Math.round(weightedSum / totalWeight);
  const lossToleranceScore = subScore(byCat.lossTolerance);
  const riskCapacityScore  = subScore(byCat.riskCapacity);
  const rothContextScore   = subScore(byCat.rothContext);
  const q8Score = RISK_QUESTIONS.find(q => q.id === 'q8').options[answers.q8].score;

  return {
    composite,
    band: bandFor(composite),
    lossToleranceScore,
    riskCapacityScore,
    rothContextScore,
    ceSignal: ceSignalFor(q8Score),
  };
}

// One-paragraph advisor narrative driven by the three sub-score levels.
export function riskNarrative(rs, clientName) {
  const who = clientName && clientName.trim() ? clientName.trim() : 'This client';
  const lvl = s => (s < 25 ? 'low' : s < 50 ? 'moderate' : s < 75 ? 'elevated' : 'high');

  const loss = {
    low:      'are strongly loss-averse and likely to react to short-term declines, so portfolio volatility should be tightly managed',
    moderate: 'can tolerate modest volatility but may grow uneasy during sharp downturns',
    elevated: 'are comfortable accepting meaningful volatility in pursuit of stronger long-term returns',
    high:     'embrace volatility and tend to view large drawdowns as buying opportunities',
  }[lvl(rs.lossToleranceScore)];

  const cap = {
    low:      'limited capacity to absorb losses — this account carries most of their retirement security and/or a near-term draw',
    moderate: 'moderate capacity to take risk, with some buffer outside this account',
    elevated: 'solid capacity to take risk, with a longer runway and assets beyond this account',
    high:     'substantial capacity to take risk, with a long horizon and significant assets outside this account',
  }[lvl(rs.riskCapacityScore)];

  const roth = {
    low:      'they are relatively tax-sanguine and value stability over conversion-driven volatility',
    moderate: 'they see moderate value in converting but want a measured, well-explained approach',
    elevated: 'they are tax-motivated and open to a yield-funded conversion approach',
    high:     'they are highly tax-motivated and comfortable with a market-funded conversion strategy',
  }[lvl(rs.rothContextScore)];

  return `${who} scores ${rs.composite} overall (${rs.band}). On loss tolerance they ${loss}. ` +
    `They have ${cap}. On Roth conversions, ${roth}. ${rs.ceSignal.text}.`;
}
